import React from 'react';
import { ItemGrid, type ItemData } from '../../../components/ItemKit';
import type { BankActions, BankGoldNotice, BankState, BankUnavailableReason } from '../../../hooks/useBank';
import { useHudTomeVibe } from '../../../HUD/useHudTomeVibe';
import type { InventoryItem } from '../../../../../shared/CharacterTypes';
import { BankGoldForm } from './BankGoldForm';
import { BankSatchel, SATCHEL_GAP, SATCHEL_PAD } from './BankSatchel';
import './Bank.css';

/**
 * BankView — the Bank window body, presentational: vault ↔ inventory transfer
 * from props. BankContent (../BankWindow.tsx) wires useBank + useInventory;
 * the offline gallery renders it from fixtures.
 *
 * Tome look (lane AN): the body wears the current zone's tome vibe (tokens
 * from primitives/tome/tome-tokens.css), each side is a parchment leaf with
 * its purse on top — balance in gilt figures, a typed amount, All, and the
 * action (BankGoldForm) — over a leather satchel holding the item grid.
 * Double-click or right-click transfers entire stacks between bag and vault.
 */

export interface BankViewInventory {
  items: InventoryItem[];
  gold: number;
  maxSlots: number;
}

export interface BankViewProps {
  bank: BankState & BankActions;
  inventory: BankViewInventory;
  /** Zone id for the tome vibe; unknown or null wears `default`. */
  zoneId?: string | null;
  /** Dev/QA: show the Grant test gear button. */
  showTestGear?: boolean;
}

const UNAVAILABLE_COPY: Record<BankUnavailableReason, string> = {
  'no-server-protocol':
    'Deposits and withdrawals are not available yet. Your items and gold stay in your bag.',
  'no-local-producer':
    'Your vault is unavailable in this session. Deposits and withdrawals remain disabled.',
};

const GRID_COLUMNS = 5;
/** Five 32 px slots fit a leaf at the window's first-open width (500 px). */
const GRID_STYLE: React.CSSProperties = { padding: SATCHEL_PAD, gap: SATCHEL_GAP };

/** Map a gameplay InventoryItem onto the ItemKit display shape. */
const KIT_RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary'] as const;
function toItemData(item: InventoryItem): ItemData {
  const rarity =
    item.rarity && (KIT_RARITIES as readonly string[]).includes(item.rarity)
      ? (item.rarity as (typeof KIT_RARITIES)[number])
      : undefined;
  return {
    id: item.id,
    name: item.name,
    icon: item.icon,
    rarity,
    quantity: item.quantity,
    type: item.type,
    description: item.description,
  };
}

function noticeFor(notice: BankGoldNotice | null | undefined, action: BankGoldNotice['action']) {
  return notice && notice.action === action ? notice : null;
}

export const BankView: React.FC<BankViewProps> = ({ bank, inventory, zoneId = null, showTestGear = false }) => {
  const {
    bankItems, bankGold, bankMaxSlots, available, unavailableReason, goldNotice,
    deposit, withdraw, depositGold, withdrawGold, grantTestGear,
  } = bank;
  const { items: inventoryItems, gold: inventoryGold, maxSlots: inventoryMaxSlots } = inventory;
  const tokens = useHudTomeVibe(zoneId);

  const inventoryCapacity = Math.max(inventoryItems.length, inventoryMaxSlots);
  const vaultCapacity = available ? Math.max(bankItems.length, bankMaxSlots) : 0;

  const depositStack = (item: ItemData | null) => {
    if (!item) return;
    deposit(item.id, item.quantity ?? 1);
  };

  const withdrawStack = (item: ItemData | null) => {
    if (!item) return;
    withdraw(item.id, item.quantity ?? 1);
  };

  return (
    <div className={`bank ${tokens.className}`} style={tokens.style} data-testid="bank-content" data-bank-available={String(available)}>
      {!available && (
        <div className="bank-notice" role="status" data-testid="bank-unavailable-notice">
          <span className="bank-notice-title">Bank unavailable</span>
          <span>{unavailableReason ? UNAVAILABLE_COPY[unavailableReason] : 'Bank transfers are unavailable in this session.'}</span>
        </div>
      )}
      <div className="bank-columns" aria-disabled={!available}>
        <section className="bank-leaf" aria-label="Inventory">
          <header className="bank-leaf-head">
            <h2 className="bank-leaf-title">Inventory</h2>
            <span className="bank-leaf-count" data-testid="bank-inventory-count">{inventoryItems.length} / {inventoryCapacity}</span>
          </header>
          <BankGoldForm direction="deposit" balance={inventoryGold} disabled={!available}
            onSubmit={depositGold} notice={noticeFor(goldNotice, 'depositGold')} />
          <BankSatchel data-testid="bank-inventory-grid">
            <ItemGrid
              items={inventoryItems.map(toItemData)}
              columns={GRID_COLUMNS}
              style={GRID_STYLE}
              slotSize="sm"
              maxSlots={inventoryCapacity}
              onSlotDoubleClick={available ? depositStack : undefined}
              onSlotRightClick={available ? depositStack : undefined}
            />
          </BankSatchel>
        </section>
        <section className="bank-leaf" aria-label="Vault">
          <header className="bank-leaf-head">
            <h2 className="bank-leaf-title">Vault</h2>
            {available && <span className="bank-leaf-count" data-testid="bank-vault-count">{bankItems.length} / {vaultCapacity}</span>}
          </header>
          {available
            ? <BankGoldForm direction="withdraw" balance={bankGold}
              onSubmit={withdrawGold} notice={noticeFor(goldNotice, 'withdrawGold')} />
            : <p className="bank-purse bank-purse-unavailable">Vault balance unavailable</p>}
          <BankSatchel data-testid="bank-vault-grid">
            <ItemGrid
              items={available ? bankItems.map(toItemData) : []}
              columns={GRID_COLUMNS}
              style={GRID_STYLE}
              slotSize="sm"
              maxSlots={vaultCapacity}
              onSlotDoubleClick={available ? withdrawStack : undefined}
              onSlotRightClick={available ? withdrawStack : undefined}
            />
          </BankSatchel>
        </section>
      </div>
      {available && (
        <footer className="bank-foot">
          <span data-testid="bank-transfer-hint">Double-click an item in your bag to deposit it, or a vault item to withdraw it.</span>
          {showTestGear && (
            <button type="button" className="bank-purse-chip" data-testid="bank-test-gear" onClick={grantTestGear}>Grant test gear</button>
          )}
        </footer>
      )}
    </div>
  );
};
